import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getSupabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth/useAuth';
import { navigate } from '@/lib/router/useHashRoute';
import { readLastSeen } from '@/lib/conversations/lastSeen';
import { getLocalizedRoleLabel } from '@/lib/i18n/roleLabel';
import { useRoleTranslationRefresh } from '@/lib/i18n/useRoleTranslationRefresh';
import { isLang, type Lang } from '@/types/lang';

/**
 * ContactList — Family (CLAUDE.md §6.4)
 *
 * Lists every other family member visible to the current user via RLS
 * (`users read self and same-conversation members` — see migration 0002).
 * Self is filtered client-side so the list is purely "other people".
 *
 * Tap → resolves to a 1-on-1 conversation:
 *   - Looks for an existing direct conversation with both members.
 *   - If none, calls the `conversation-find-or-create` Edge Function (service
 *     role inserts both `conversations` and the two `conversation_members` rows
 *     — clients can't insert into either table directly, see RLS in 0002).
 *
 * Avatar = initials on a deterministic warm color from user_id (CLAUDE.md §7).
 * No profile photos in Phase 1.
 *
 * `compact` (A6 — iPad two-pane): renders smaller avatars + truncated rows for
 * the sidebar. The default phone layout remains unchanged.
 * `activeConversationId` (A6): when provided, the row whose conversation matches
 * is rendered with a highlighted state — used by the iPad two-pane layout so the
 * sidebar reflects the chat currently open in the right pane.
 */

interface ContactRow {
  id: string;
  name: string;
  role_label: string;
  role_label_translations: Record<string, string> | null;
}

interface LastMessageInfo {
  body: string;
  source_lang: string;
  created_at: string;
  isMine: boolean;
  isUnread: boolean;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Editorial monogram gradients — replaces the v2.2 saturated avatar palette.
 * Each contact gets a deterministic champagne+rose gradient pair so the warm
 * cream→pink wedding feel persists across the avatar field. All variants pair
 * with the Playfair serif initial inside `.monogram-avatar` (see styles.css).
 */
const MONOGRAM_GRADIENTS: Array<{ background: string; color: string }> = [
  // dusty pink → champagne — bride family vibe
  { background: 'linear-gradient(135deg, #F5DCDC 0%, #D4AF8B 100%)', color: '#8B3A4B' },
  // ivory → champagne — groom family vibe
  { background: 'linear-gradient(135deg, #F0E8D9 0%, #A88660 100%)', color: '#FFFFFF' },
  // cream → soft gold — neutral elder
  { background: 'linear-gradient(135deg, #F1E2C7 0%, #D4AF8B 100%)', color: '#8B3A4B' },
  // peach blush — younger sibling
  { background: 'linear-gradient(135deg, #FBE2D6 0%, #D4AF8B 100%)', color: '#8B3A4B' },
  // muted rose — close cousin
  { background: 'linear-gradient(135deg, #E8DCC4 0%, #C77B2D 100%)', color: '#FFFFFF' },
];

function monogramGradient(userId: string): { background: string; color: string } {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = (hash * 31 + userId.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(hash) % MONOGRAM_GRADIENTS.length;
  return MONOGRAM_GRADIENTS[idx] ?? MONOGRAM_GRADIENTS[0]!;
}

function monogram(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '·';
  return trimmed.charAt(0).toUpperCase();
}

interface ContactListProps {
  /** Smaller avatars + tighter padding for sidebar use. */
  compact?: boolean;
  /**
   * When set, the row whose conversation matches receives an "active" border —
   * used by the iPad two-pane layout so the sidebar reflects which chat is
   * currently in the right pane.
   *
   * NOTE: we don't know the conversation id from the contact id alone; the
   * tablet layout uses `?c=<convId>` and passes that through. Comparison
   * happens when the open-chat mutation has already resolved.
   */
  activeConversationId?: string | null;
  /**
   * Custom navigation callback. Default: `navigate('/chat/<convId>')`.
   * The tablet two-pane layout overrides this with a `?c=<convId>` query
   * string so the path stays on `/` (Home stays mounted in main).
   */
  onOpenConversation?: (conversationId: string) => void;
}

export function ContactList({
  compact = false,
  activeConversationId = null,
  onOpenConversation,
}: ContactListProps = {}) {
  const { t, i18n } = useTranslation();
  const uiLang: Lang = isLang(i18n.language) ? i18n.language : 'tr';
  const { session } = useAuth();
  const myId = session?.user.id ?? null;

  const contactsQuery = useQuery({
    queryKey: ['contacts', myId],
    enabled: Boolean(myId),
    queryFn: async (): Promise<ContactRow[]> => {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from('users')
        .select('id, name, role_label, role_label_translations')
        .order('name', { ascending: true });
      if (error) throw error;
      const rows = (data ?? []).map<ContactRow>((u) => ({
        id: u.id as string,
        name: u.name as string,
        role_label: u.role_label as string,
        role_label_translations:
          u.role_label_translations && typeof u.role_label_translations === 'object'
            ? (u.role_label_translations as Record<string, string>)
            : null,
      }));
      return rows.filter((u) => u.id !== myId);
    },
    refetchInterval: 8000, // Oturum 8: role fanout async, ilk 5-10sn'de translations doldukça polluyor
  });

  // Bug 10 fix (Oturum 7): Her contact için son mesaj preview + unread işareti.
  // Önceden ContactList sadece name+role gösteriyordu, mesaj gelince Home'da
  // farkındalık yok diye Begüm haklı şikayet etti.
  // Strategy: tek seferde tüm conv'ları + son mesajları al, contact.id'ye eşle.
  const lastMessagesQuery = useQuery({
    queryKey: ['contact-last-messages', myId, uiLang],
    enabled: Boolean(myId),
    staleTime: 30_000,
    queryFn: async (): Promise<{
      byContact: Record<string, LastMessageInfo>;
      convByContact: Record<string, string>;
    }> => {
      if (!myId) return { byContact: {}, convByContact: {} };
      const supabase = getSupabase();

      // 1. Kullanıcının tüm conv_id'leri
      const { data: myConvRows } = await supabase
        .from('conversation_members')
        .select('conversation_id')
        .eq('user_id', myId);
      const convIds = (myConvRows ?? []).map((r) => r['conversation_id'] as string);
      if (convIds.length === 0) return { byContact: {}, convByContact: {} };

      // 2. Bu conv'lardaki tüm üyeleri al (other user mapping için)
      const { data: allMembers } = await supabase
        .from('conversation_members')
        .select('conversation_id, user_id')
        .in('conversation_id', convIds);
      const otherByConv = new Map<string, string>();
      for (const m of allMembers ?? []) {
        const uid = m['user_id'] as string;
        const cid = m['conversation_id'] as string;
        if (uid !== myId) otherByConv.set(cid, uid);
      }

      // 3. Tüm conv'lardaki son mesajları al + message_translations join.
      // Bug B fix (Oturum 8): recipient için son mesaj preview kendi UI
      // dilinde olmalı, source_lang'inde değil. Eğer mesaj benimse orijinal
      // göster (kendi yazdığım), değilse target_lang=myUiLang çevirisi.
      const { data: msgs } = await supabase
        .from('messages')
        .select(
          'conversation_id, sender_id, body, source_lang, created_at, message_translations(target_lang, body)',
        )
        .in('conversation_id', convIds)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(200);

      const byConv = new Map<
        string,
        { body: string; source_lang: string; created_at: string; sender_id: string }
      >();
      for (const m of msgs ?? []) {
        const cid = m['conversation_id'] as string;
        if (byConv.has(cid)) continue;
        const senderId = m['sender_id'] as string;
        const translations = m['message_translations'] as
          | Array<{ target_lang: string; body: string }>
          | null;
        const myTranslation = translations?.find((t) => t.target_lang === uiLang);
        const isMine = senderId === myId;
        const previewBody = isMine ? (m['body'] as string) : myTranslation?.body ?? (m['body'] as string);
        byConv.set(cid, {
          body: previewBody,
          source_lang: m['source_lang'] as string,
          created_at: m['created_at'] as string,
          sender_id: senderId,
        });
      }

      // 4. contact.id (other user) → LastMessageInfo + conv mapping
      const byContact: Record<string, LastMessageInfo> = {};
      const convByContact: Record<string, string> = {};
      for (const [cid, info] of byConv) {
        const otherId = otherByConv.get(cid);
        if (!otherId) continue;
        const lastSeen = readLastSeen(cid);
        const msgTime = new Date(info.created_at);
        const isMine = info.sender_id === myId;
        // Mesaj benim değilse VE lastSeen'den yeni ise unread.
        const isUnread = !isMine && (!lastSeen || msgTime > lastSeen);
        byContact[otherId] = {
          body: info.body,
          source_lang: info.source_lang,
          created_at: info.created_at,
          isMine,
          isUnread,
        };
        convByContact[otherId] = cid;
      }
      return { byContact, convByContact };
    },
  });

  const openChat = useMutation({
    mutationFn: async (otherUserId: string): Promise<string> => {
      const supabase = getSupabase();
      const result = await supabase.functions.invoke<{ conversationId: string }>(
        'conversation-find-or-create',
        { body: { otherUserId } },
      );
      if (result.error || !result.data) {
        throw (result.error ?? new Error('No conversation id returned')) as Error;
      }
      const conversationId = result.data.conversationId;
      if (typeof conversationId !== 'string' || conversationId.length === 0) {
        throw new Error('Edge Function returned empty conversationId');
      }
      return conversationId;
    },
    onSuccess: (conversationId) => {
      if (onOpenConversation) {
        onOpenConversation(conversationId);
      } else {
        navigate(`/chat/${conversationId}`);
      }
    },
  });

  // Oturum 9 00:10 — Background auto-refresh: bir member'ın
  // role_label_translations[viewerLang]'i boşsa Edge Function tetikleyip
  // arka planda fanout yeniden çalıştır. Reader birkaç saniye sonra
  // (TanStack Query refetchInterval 8sn) güncel çeviriyi görür.
  // Hook'u early-return'lerden ÖNCE çağır (React rules-of-hooks).
  useRoleTranslationRefresh(contactsQuery.data, uiLang);

  if (contactsQuery.isLoading) {
    return (
      <div role="status" aria-live="polite" className="text-base text-text-secondary py-4">
        …
      </div>
    );
  }

  if (contactsQuery.isError) {
    return (
      <div role="alert" className="text-base text-text-secondary py-4">
        {t('errors.unknown')}
      </div>
    );
  }

  const contacts = contactsQuery.data ?? [];

  if (contacts.length === 0) {
    return (
      <div className="text-base text-text-secondary py-4">{t('home.noContactsYet')}</div>
    );
  }

  const avatarSize = compact ? 'w-10 h-10 text-base' : 'w-12 h-12 text-lg';
  const padding = compact ? 'px-3 py-2' : 'px-4 py-3';

  return (
    <ul
      className="flex flex-col rounded-2xl border border-border-subtle bg-bg-elevated overflow-hidden shadow-warm-sm"
      aria-busy={openChat.isPending}
    >
      {contacts.map((contact, idx) => {
        const isActive =
          Boolean(activeConversationId) && openChat.variables === contact.id;
        const gradient = monogramGradient(contact.id);
        const isLast = idx === contacts.length - 1;
        return (
          <li key={contact.id}>
            <button
              type="button"
              onClick={() => openChat.mutate(contact.id)}
              disabled={openChat.isPending}
              aria-current={isActive ? 'page' : undefined}
              className={`w-full min-h-tap flex items-center gap-4 ${padding} text-left transition-colors duration-150 disabled:opacity-50 ${
                !isLast ? 'border-b border-border-subtle' : ''
              } ${
                isActive
                  ? 'bg-bg-bubble-received/40'
                  : 'hover:bg-bg-bubble-received/30'
              }`}
            >
              <span
                aria-hidden="true"
                className={`monogram-avatar ${avatarSize}`}
                style={{ background: gradient.background, color: gradient.color }}
              >
                {monogram(contact.name)}
              </span>
              <span className="flex flex-col min-w-0 flex-1">
                <span className="flex items-center gap-2 min-w-0">
                  <span
                    className={`${
                      compact ? 'text-base' : 'text-lg'
                    } font-serif text-text-primary truncate leading-tight`}
                  >
                    {contact.name}
                  </span>
                  {(() => {
                    const last = lastMessagesQuery.data?.byContact[contact.id];
                    if (!last) return null;
                    return (
                      <span
                        aria-hidden="true"
                        className="text-xs text-text-secondary font-serif italic ms-auto flex-shrink-0"
                      >
                        {formatTime(last.created_at)}
                      </span>
                    );
                  })()}
                </span>
                {(() => {
                  const last = lastMessagesQuery.data?.byContact[contact.id];
                  if (!last) {
                    return (
                      <span className="text-sm text-text-secondary truncate font-serif italic mt-0.5">
                        {getLocalizedRoleLabel(contact, uiLang)}
                      </span>
                    );
                  }
                  const prefix = last.isMine ? `${t('home.youPrefix')} ` : '';
                  return (
                    <span className="flex items-center gap-2 min-w-0">
                      <span
                        className={`text-sm truncate mt-0.5 ${
                          last.isUnread
                            ? 'text-text-primary font-semibold'
                            : 'text-text-secondary font-serif italic'
                        }`}
                      >
                        {prefix}{last.body}
                      </span>
                      {last.isUnread ? (
                        <span
                          aria-label={t('home.unreadDot')}
                          className="ms-auto w-2.5 h-2.5 rounded-full bg-accent-primary flex-shrink-0"
                        />
                      ) : null}
                    </span>
                  );
                })()}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
